import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

// Same-origin: nginx (prod) and the Vite dev proxy both route /api to the
// backend. Absolute http:// URLs would be blocked as mixed content over HTTPS.
const API_URL = window.location.origin;

// Async thunks
export const fetchFolders = createAsyncThunk(
	"folders/fetchAll",
	async (projectId, { getState }) => {
		const token = localStorage.getItem("token");
		const response = await axios.get(`${API_URL}/api/folders`, {
			params: { projectId },
			headers: { Authorization: `Bearer ${token}` },
		});
		return { projectId, folders: response.data };
	},
);

export const createFolder = createAsyncThunk(
	"folders/create",
	async (folder) => {
		const token = localStorage.getItem("token");
		const response = await axios.post(`${API_URL}/api/folders`, folder, {
			headers: { Authorization: `Bearer ${token}` },
		});
		return response.data;
	},
);

export const updateFolder = createAsyncThunk(
	"folders/update",
	async ({ id, folder }) => {
		const token = localStorage.getItem("token");
		const response = await axios.put(
			`${API_URL}/api/folders/${id}`,
			folder,
			{
				headers: { Authorization: `Bearer ${token}` },
			},
		);
		return response.data;
	},
);

export const moveFolder = createAsyncThunk(
	"folders/move",
	async ({ id, newParentFolderId }) => {
		const token = localStorage.getItem("token");
		const response = await axios.put(
			`${API_URL}/api/folders/${id}/move`,
			{},
			{
				params: { newParentFolderId },
				headers: { Authorization: `Bearer ${token}` },
			},
		);
		return response.data;
	},
);

export const deleteFolder = createAsyncThunk("folders/delete", async (id) => {
	const token = localStorage.getItem("token");
	await axios.delete(`${API_URL}/api/folders/${id}`, {
		headers: { Authorization: `Bearer ${token}` },
	});
	return id;
});

const folderSlice = createSlice({
	name: "folders",
	initialState: {
		folders: [],
		selectedFolder: null,
		loading: false,
		error: null,
	},
	reducers: {
		selectFolder: (state, action) => {
			state.selectedFolder = action.payload;
		},
		clearError: (state) => {
			state.error = null;
		},
	},
	extraReducers: (builder) => {
		builder
			// Fetch folders
			.addCase(fetchFolders.pending, (state) => {
				state.loading = true;
			})
			.addCase(fetchFolders.fulfilled, (state, action) => {
				state.loading = false;
				const { projectId, folders: newFolders } = action.payload;
				// Remove existing folders for this project and add the new ones
				state.folders = state.folders.filter(
					(f) => f.projectId !== projectId,
				);
				state.folders.push(...newFolders);
			})
			.addCase(fetchFolders.rejected, (state, action) => {
				state.loading = false;
				state.error = action.error.message;
			})
			// Create folder
			.addCase(createFolder.fulfilled, (state, action) => {
				state.folders.push(action.payload);
			})
			// Update folder
			.addCase(updateFolder.fulfilled, (state, action) => {
				const index = state.folders.findIndex(
					(f) => f.id === action.payload.id,
				);
				if (index !== -1) {
					state.folders[index] = action.payload;
				}
				if (state.selectedFolder?.id === action.payload.id) {
					state.selectedFolder = action.payload;
				}
			})
			// Move folder
			.addCase(moveFolder.fulfilled, (state, action) => {
				const index = state.folders.findIndex(
					(f) => f.id === action.payload.id,
				);
				if (index !== -1) {
					state.folders[index] = action.payload;
				}
			})
			// Delete folder
			.addCase(deleteFolder.fulfilled, (state, action) => {
				state.folders = state.folders.filter(
					(f) => f.id !== action.payload,
				);
				if (state.selectedFolder?.id === action.payload) {
					state.selectedFolder = null;
				}
			});
	},
});

export const { selectFolder, clearError } = folderSlice.actions;
export default folderSlice.reducer;
